import {
  useEntity,
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { Progress, Table, TableColumn } from '@backstage/core-components';
import { RELATION_OWNED_BY } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useState, useEffect, useMemo } from 'react';

interface RoleData {
  roleName: string;
  roleDisplayName: string;
  members: string[];
  entityRef: string;
}

export const AwsAccountRolesCard = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef) as any;
  const [roleData, setRoleData] = useState<RoleData[]>([]);
  const [loading, setLoading] = useState(true);

  const roleGroupRelations = useMemo(() => {
    return getEntityRelations(entity, RELATION_OWNED_BY);
  }, [entity]);

  useEffect(() => {
    const fetchRoleData = async () => {
      if (roleGroupRelations.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const mainGroupRef = roleGroupRelations[0];
        const mainGroup = await catalogApi.getEntityByRef(mainGroupRef);

        if (!mainGroup || !mainGroup.spec?.children) {
          setLoading(false);
          return;
        }

        const childGroupPromises = mainGroup.spec.children.map(async (childRef: string) => {
          try {
            const childGroup = await catalogApi.getEntityByRef(childRef);
            if (!childGroup) return null;

            const roleName = childGroup.metadata.name;
            const roleDisplayName = childGroup.metadata.title || childGroup.spec?.profile?.displayName || roleName;
            const members = childGroup.spec?.members || [];

            return {
              roleName,
              roleDisplayName,
              members,
              entityRef: childRef,
            };
          } catch {
            return null;
          }
        });

        const childGroups = await Promise.all(childGroupPromises);
        const validChildGroups = childGroups.filter(
          (group: RoleData | null): group is RoleData => group !== null,
        );

        setRoleData(validChildGroups);
      } catch {
        // Silently handle errors
      } finally {
        setLoading(false);
      }
    };

    fetchRoleData();
  }, [catalogApi, roleGroupRelations]);

  if (loading) {
    return <Progress />;
  }

  if (roleData.length === 0) {
    return <div>No role information available for this account.</div>;
  }

  return (
    <Table
      title="AWS Account Roles & Members"
      options={{
        paging: false,
        search: false,
        padding: 'dense',
        columnsButton: true,
      }}
      data={roleData}
      columns={[
        {
          title: 'Role',
          field: 'entityRef',
          width: '15%',
          render: (row: RoleData) => (
            <EntityRefLinks
              entityRefs={[row.entityRef]}
              defaultKind="group"
            />
          ),
        },
        {
          title: 'Members',
          field: 'members',
          width: '75%',
          render: (row: RoleData) =>
            row.members.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {row.members.map(member => (
                  <EntityRefLinks
                    key={member}
                    entityRefs={[member]}
                    defaultKind="user"
                  />
                ))}
              </div>
            ) : (
              <em>No members</em>
            ),
        },
        {
          title: 'Total Members',
          field: 'members',
          width: '10%',
          render: (row: RoleData) => row.members.length,
        },
      ] as TableColumn<RoleData>[]}
    />
  );
};
